import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Brain,
  Upload,
  Globe,
  FileText,
  Trash2,
  RefreshCw,
  Plus,
  Check,
  Clock,
  AlertCircle,
  X,
  Zap,
  Link2,
  Eye,
  Play,
  Pause,
  History,
  Settings,
  TrendingUp,
  TrendingDown,
  Lightbulb,
  BarChart3,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { notify } from '../store/notifications';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Platform Insights Types
interface PlatformInsight {
  platform: string;
  status: 'healthy' | 'needs_attention' | 'critical';
  score: number;
  metrics: { contentCount: number; lastActivity: string | null; engagement: number; recentPosts: number; };
  suggestions: { type: 'warning' | 'info' | 'success' | 'action'; title: string; description: string; priority: 'high' | 'medium' | 'low'; }[];
}

interface InsightsData {
  overallScore: number;
  overallStatus: 'healthy' | 'needs_attention' | 'critical';
  platforms: PlatformInsight[];
  criticalSuggestions: { type: string; title: string; description: string; priority: string; platform: string; }[];
  lastAnalyzed: string;
}

interface BrainDocument {
  id: number;
  name: string;
  type: string;
  source_url?: string;
  status: 'processing' | 'ready' | 'error';
  chunks_count: number;
  content_summary?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

interface BrainConfig {
  custom_instructions: string | null;
  tone: string;
  language: string;
  response_length: string;
}

interface AutoTrainingConfig {
  auto_training_enabled: boolean;
  auto_training_sources: string[];
  auto_training_interval: string;
  last_auto_sync: string | null;
}

interface ConnectedIntegration {
  id: number;
  provider: string;
  status: string;
}

interface SyncHistory {
  id: number;
  provider: string;
  status: string;
  items_synced: number | null;
  error_message: string | null;
  created_at: string;
}

interface BrainStats {
  total_documents: number;
  total_chunks: number;
  ready_count: number;
  processing_count: number;
  error_count: number;
  last_updated: string | null;
}

export default function BrainPage() {
  const { token } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState<'documents' | 'auto-training' | 'config'>('documents');
  const [documents, setDocuments] = useState<BrainDocument[]>([]);
  const [stats, setStats] = useState<BrainStats | null>(null);
  const [config, setConfig] = useState<BrainConfig>({
    custom_instructions: null,
    tone: 'professional',
    language: 'en',
    response_length: 'balanced',
  });
  const [autoTrainingConfig, setAutoTrainingConfig] = useState<AutoTrainingConfig>({
    auto_training_enabled: false,
    auto_training_sources: [],
    auto_training_interval: 'daily',
    last_auto_sync: null,
  });
  const [connectedIntegrations, setConnectedIntegrations] = useState<ConnectedIntegration[]>([]);
  const [syncHistory, setSyncHistory] = useState<SyncHistory[]>([]);
  const [insights, setInsights] = useState<InsightsData | null>(null);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [isSavingAutoTraining, setIsSavingAutoTraining] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch documents and stats (called on polling)
  const fetchDocumentsAndStats = useCallback(async () => {
    if (!token) return;

    try {
      const [docsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/brain/documents`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/brain/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (docsRes.ok) {
        const docsData = await docsRes.json() as { success: boolean; data: { items: BrainDocument[] } };
        if (docsData.success) {
          setDocuments(docsData.data.items);
        }
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json() as { success: boolean; data: BrainStats };
        if (statsData.success) {
          setStats(statsData.data);
        }
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    }
  }, [token]);

  // Full data fetch (only called on initial load)
  const fetchData = useCallback(async () => {
    if (!token) return;

    try {
      const [docsRes, statsRes, configRes, autoConfigRes, integrationsRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/brain/documents`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/brain/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/brain/config`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/brain/sync/config`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/integrations`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/brain/sync/history`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (docsRes.ok) {
        const docsData = await docsRes.json() as { success: boolean; data: { items: BrainDocument[] } };
        if (docsData.success) {
          setDocuments(docsData.data.items);
        }
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json() as { success: boolean; data: BrainStats };
        if (statsData.success) {
          setStats(statsData.data);
        }
      }

      if (configRes.ok) {
        const configData = await configRes.json() as { success: boolean; data: BrainConfig };
        if (configData.success) {
          setConfig(configData.data);
        }
      }

      if (autoConfigRes.ok) {
        const autoData = await autoConfigRes.json() as { success: boolean; data: AutoTrainingConfig };
        if (autoData.success && autoData.data) {
          setAutoTrainingConfig(autoData.data);
        }
      }

      if (integrationsRes.ok) {
        const intData = await integrationsRes.json() as { success: boolean; data: ConnectedIntegration[] };
        if (intData.success) {
          setConnectedIntegrations(intData.data.filter(i => i.status === 'connected'));
        }
      }

      if (historyRes.ok) {
        const histData = await historyRes.json() as { success: boolean; data: SyncHistory[] };
        if (histData.success) {
          setSyncHistory(histData.data);
        }
      }
    } catch (err) {
      console.error('Failed to fetch brain data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Fetch platform insights
  const fetchInsights = useCallback(async () => {
    if (!token) return;
    setIsLoadingInsights(true);
    try {
      const res = await fetch(`${API_URL}/brain/sync/insights`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json() as { success: boolean; data: InsightsData };
        if (data.success) {
          setInsights(data.data);
          // Show notification for critical suggestions
          if (data.data.criticalSuggestions?.length > 0) {
            const first = data.data.criticalSuggestions[0];
            notify.info(`${first.platform} Needs Attention`, first.description, '/dashboard/brain');
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch insights:', err);
    } finally {
      setIsLoadingInsights(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch insights when auto-training tab is active
  useEffect(() => {
    if (activeTab === 'auto-training' && connectedIntegrations.length > 0) {
      fetchInsights();
    }
  }, [activeTab, connectedIntegrations.length, fetchInsights]);

  // Poll for processing documents - faster updates (only docs/stats, not config)
  useEffect(() => {
    const hasProcessing = documents.some((d) => d.status === 'processing');
    if (!hasProcessing) return;

    // Poll every 1.5 seconds for real-time feel
    const interval = setInterval(fetchDocumentsAndStats, 1500);
    return () => clearInterval(interval);
  }, [documents, fetchDocumentsAndStats]);

  // Also poll periodically even without processing documents for real-time updates
  useEffect(() => {
    const interval = setInterval(fetchDocumentsAndStats, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [fetchDocumentsAndStats]);

  // Track document status changes for notifications
  const prevDocumentsRef = useRef<BrainDocument[]>([]);
  useEffect(() => {
    const prevDocs = prevDocumentsRef.current;

    // Check for status changes
    documents.forEach((doc) => {
      const prevDoc = prevDocs.find((d) => d.id === doc.id);
      if (prevDoc && prevDoc.status === 'processing' && doc.status === 'ready') {
        notify.success('Document Ready', `"${doc.name}" has been processed and is now ready to use.`, '/dashboard/brain');
      } else if (prevDoc && prevDoc.status === 'processing' && doc.status === 'error') {
        notify.error('Processing Failed', `Failed to process "${doc.name}": ${doc.error_message || 'Unknown error'}`);
      }
    });

    prevDocumentsRef.current = documents;
  }, [documents]);

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !token) return;

    setIsUploading(true);
    setError(null);

    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_URL}/brain/documents`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });

        const data = await response.json() as { success: boolean; error?: string };

        if (!response.ok) {
          throw new Error(data.error || 'Upload failed');
        }
      }

      setSuccessMessage('Document(s) uploaded successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      notify.success('Document Uploaded', 'Your document is being processed and will be ready shortly.', '/dashboard/brain');
      fetchData();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Upload failed';
      setError(errorMsg);
      notify.error('Upload Failed', errorMsg);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAddUrl = async () => {
    if (!urlInput.trim() || !token) return;

    setIsUploading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/brain/documents`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ source_url: urlInput }),
      });

      const data = await response.json() as { success: boolean; error?: string };

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add URL');
      }

      setUrlInput('');
      setSuccessMessage('URL added successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      notify.success('URL Added', 'The website content is being crawled and processed.', '/dashboard/brain');
      fetchData();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to add URL';
      setError(errorMsg);
      notify.error('URL Failed', errorMsg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    if (!token) return;
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await fetch(`${API_URL}/brain/documents/${documentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      setSuccessMessage('Document deleted');
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const handleReprocessDocument = async (documentId: number) => {
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/brain/documents/${documentId}/reprocess`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to reprocess document');
      }

      setSuccessMessage('Document queued for reprocessing');
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reprocess failed');
    }
  };

  const handleSaveConfig = async () => {
    if (!token) return;

    setIsSavingConfig(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/brain/config`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        throw new Error('Failed to save configuration');
      }

      setSuccessMessage('Configuration saved!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleSaveAutoTraining = async () => {
    if (!token) return;

    setIsSavingAutoTraining(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/brain/sync/config`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(autoTrainingConfig),
      });

      if (!response.ok) {
        throw new Error('Failed to save auto-training configuration');
      }

      setSuccessMessage('Auto-training configuration saved!');
      setTimeout(() => setSuccessMessage(null), 3000);
      notify.success('Auto-Training Updated', 'Your auto-training settings have been saved.', '/dashboard/brain');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsSavingAutoTraining(false);
    }
  };

  const handleManualSync = async (provider?: string) => {
    if (!token) return;

    setIsSyncing(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/brain/sync/sync`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ provider }),
      });

      const data = await response.json() as { success: boolean; error?: string; message?: string };

      if (!response.ok) {
        throw new Error(data.error || 'Sync failed');
      }

      setSuccessMessage(data.message || 'Sync completed successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      notify.success('Sync Complete', provider ? `Synced ${provider} successfully.` : 'Synced all sources successfully.', '/dashboard/brain');

      // Refresh documents and sync history (not the auto-training config which user may have modified)
      fetchDocumentsAndStats();

      // Also fetch sync history
      const historyRes = await fetch(`${API_URL}/brain/sync/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (historyRes.ok) {
        const histData = await historyRes.json() as { success: boolean; data: SyncHistory[] };
        if (histData.success) {
          setSyncHistory(histData.data);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setIsSyncing(false);
    }
  };

  const toggleSource = (provider: string) => {
    setAutoTrainingConfig(prev => {
      const sources = prev.auto_training_sources || [];
      const newSources = sources.includes(provider)
        ? sources.filter(s => s !== provider)
        : [...sources, provider];
      return { ...prev, auto_training_sources: newSources };
    });
  };

  const getProviderIcon = (provider: string): string | null => {
    // Using brand logos from reliable CDNs
    const icons: Record<string, string> = {
      slack: 'https://upload.wikimedia.org/wikipedia/commons/d/d5/Slack_icon_2019.svg',
      gmail: 'https://upload.wikimedia.org/wikipedia/commons/7/7e/Gmail_icon_%282020%29.svg',
      notion: 'https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png',
      trello: 'https://upload.wikimedia.org/wikipedia/en/8/8c/Trello_logo.svg',
      twitter: 'https://upload.wikimedia.org/wikipedia/commons/6/6f/Logo_of_Twitter.svg',
      google_calendar: 'https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg',
      shopify: 'https://upload.wikimedia.org/wikipedia/commons/0/0e/Shopify_logo_2018.svg',
      asana: 'https://upload.wikimedia.org/wikipedia/commons/3/3b/Asana_logo.svg',
      hubspot: 'https://upload.wikimedia.org/wikipedia/commons/3/3f/HubSpot_Logo.svg',
    };
    return icons[provider] || null;
  };

  const getProviderLabel = (provider: string) => {
    const labels: Record<string, string> = {
      slack: 'Slack',
      gmail: 'Gmail',
      notion: 'Notion',
      trello: 'Trello',
      twitter: 'Twitter/X',
      google_calendar: 'Google Calendar',
      shopify: 'Shopify',
      asana: 'Asana',
      hubspot: 'HubSpot',
    };
    return labels[provider] || provider;
  };

  const formatDate = (dateString: string) => {
    // Handle SQLite datetime format (YYYY-MM-DD HH:MM:SS) and ISO format
    let date: Date;
    if (dateString.includes('T')) {
      date = new Date(dateString);
    } else {
      // SQLite format: "2024-12-08 10:30:00" - append Z for UTC
      date = new Date(dateString.replace(' ', 'T') + 'Z');
    }

    // Check for invalid date
    if (isNaN(date.getTime())) {
      return 'Unknown';
    }

    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-purple"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={fadeInUp}>
        <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Brain AI</h1>
        <p className={isDark ? 'text-text-secondary' : 'text-slate-500'}>
          Train your AI employees with your business knowledge
        </p>
      </motion.div>

      {/* Error/Success Messages */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-accent-red/10 border border-accent-red/20 rounded-xl p-4 flex items-center justify-between"
        >
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-accent-red" />
            <span className="text-accent-red">{error}</span>
          </div>
          <button onClick={() => setError(null)}>
            <X className="w-5 h-5 text-accent-red" />
          </button>
        </motion.div>
      )}

      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-accent-green/10 border border-accent-green/20 rounded-xl p-4 flex items-center space-x-3"
        >
          <Check className="w-5 h-5 text-accent-green" />
          <span className="text-accent-green">{successMessage}</span>
        </motion.div>
      )}

      {/* Stats */}
      <motion.div variants={fadeInUp} className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        {[
          { label: 'Documents', value: stats?.total_documents || 0, icon: FileText },
          { label: 'Total Chunks', value: stats?.total_chunks || 0, icon: Brain },
          { label: 'Last Updated', value: stats?.last_updated ? formatDate(stats.last_updated) : 'Never', icon: Clock },
        ].map((stat) => (
          <div key={stat.label} className={`p-4 sm:p-5 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-accent-purple/10 flex items-center justify-center flex-shrink-0">
                <stat.icon className="w-5 h-5 text-accent-purple" />
              </div>
              <div className="min-w-0">
                <p className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{stat.value}</p>
                <p className={`text-xs sm:text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Tabs */}
      <motion.div variants={fadeInUp} className={`flex space-x-1 rounded-xl p-1 overflow-x-auto ${isDark ? 'bg-dark-800' : 'bg-slate-100'}`}>
        {[
          { id: 'documents', label: 'Documents', icon: FileText },
          { id: 'auto-training', label: 'Auto-Training', icon: Zap },
          { id: 'config', label: 'Config', icon: Settings },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as 'documents' | 'auto-training' | 'config')}
            className={`flex-1 py-2 px-2 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-1 sm:gap-2 whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-gradient-accent text-white'
                : isDark ? 'text-text-secondary hover:text-white' : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden xs:inline sm:inline">{tab.label}</span>
          </button>
        ))}
      </motion.div>

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <motion.div variants={fadeInUp} className="space-y-4">
          {/* Upload Area */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFileUpload(e.dataTransfer.files);
            }}
            className={`p-6 rounded-2xl border-2 border-dashed transition-colors cursor-pointer ${isDark ? 'bg-dark-800 border-border hover:border-accent-purple/50' : 'bg-white border-slate-200 hover:border-accent-purple/50 shadow-sm'}`}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.txt,.csv,.doc,.docx,.md,.json,.html,.xml,.xls,.xlsx"
              multiple
              onChange={(e) => handleFileUpload(e.target.files)}
            />
            <div className="text-center py-8">
              <div className="w-12 h-12 rounded-xl bg-accent-purple/10 flex items-center justify-center mx-auto mb-4">
                {isUploading ? (
                  <RefreshCw className="w-6 h-6 text-accent-purple animate-spin" />
                ) : (
                  <Upload className="w-6 h-6 text-accent-purple" />
                )}
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {isUploading ? 'Uploading...' : 'Upload Documents'}
              </h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                Drag and drop files here, or click to browse
              </p>
              <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                Supports PDF, DOC, DOCX, TXT, CSV, MD, JSON, HTML, XML, XLS, XLSX (max 50MB)
              </p>
            </div>
          </div>

          {/* Add URL */}
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Globe className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="Add website URL to crawl..."
                className={`w-full pl-12 pr-4 py-3 rounded-xl border text-sm focus:outline-none focus:border-accent-purple focus:ring-2 focus:ring-accent-purple/20 ${isDark ? 'bg-dark-800 border-border text-white placeholder-text-muted' : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddUrl();
                  }
                }}
              />
            </div>
            <button
              onClick={handleAddUrl}
              disabled={!urlInput.trim() || isUploading}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 ${isDark ? 'bg-dark-700 border border-border-light hover:bg-dark-600' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm'}`}
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          {/* Document List */}
          {documents.length === 0 ? (
            <div className={`text-center py-12 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
              <Brain className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>No documents yet</h3>
              <p className={isDark ? 'text-text-muted' : 'text-slate-500'}>
                Upload documents or add URLs to train your AI employees
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className={`p-4 sm:p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-start sm:items-center space-x-3 sm:space-x-4 min-w-0">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                        {doc.type === 'url' ? (
                          <Globe className="w-5 h-5 text-accent-cyan" />
                        ) : (
                          <FileText className="w-5 h-5 text-accent-purple" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`font-medium text-sm sm:text-base truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{doc.name}</p>
                        <p className={`text-xs sm:text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                          {doc.chunks_count} chunks • {formatDate(doc.created_at)}
                        </p>
                        {doc.content_summary && (
                          <p className={`text-xs mt-1 truncate ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                            {doc.content_summary}
                          </p>
                        )}
                        {doc.error_message && (
                          <p className="text-xs text-accent-red mt-1 truncate">
                            Error: {doc.error_message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end space-x-2 sm:space-x-3 ml-13 sm:ml-0">
                      {/* Status */}
                      <span
                        className={`badge text-xs ${
                          doc.status === 'ready'
                            ? 'bg-accent-green/20 text-accent-green'
                            : doc.status === 'processing'
                            ? 'bg-accent-orange/20 text-accent-orange'
                            : 'bg-accent-red/20 text-accent-red'
                        }`}
                      >
                        {doc.status === 'ready' && <Check className="w-3 h-3 mr-1" />}
                        {doc.status === 'processing' && (
                          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        )}
                        {doc.status === 'error' && <AlertCircle className="w-3 h-3 mr-1" />}
                        <span className="hidden xs:inline">{doc.status}</span>
                      </span>

                      {/* Actions */}
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <button
                          onClick={() => handleReprocessDocument(doc.id)}
                          className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                          title="Reprocess"
                        >
                          <RefreshCw className={`w-4 h-4 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                        </button>
                        <button
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="p-2 hover:bg-accent-red/10 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-accent-red" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Auto-Training Tab */}
      {activeTab === 'auto-training' && (
        <motion.div variants={fadeInUp} className="space-y-4 sm:space-y-6">
          {/* Enable/Disable Auto-Training */}
          <div className={`p-4 sm:p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-4 gap-3">
              <div className="flex items-center space-x-3 min-w-0">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${autoTrainingConfig.auto_training_enabled ? 'bg-accent-green/10' : isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                  {autoTrainingConfig.auto_training_enabled ? (
                    <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-accent-green" />
                  ) : (
                    <Pause className="w-5 h-5 sm:w-6 sm:h-6 text-text-muted" />
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className={`text-base sm:text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Auto-Training
                  </h3>
                  <p className={`text-xs sm:text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                    Sync from connected integrations
                  </p>
                </div>
              </div>
              <button
                onClick={() => setAutoTrainingConfig(prev => ({
                  ...prev,
                  auto_training_enabled: !prev.auto_training_enabled
                }))}
                className={`relative w-12 h-6 sm:w-14 sm:h-7 rounded-full transition-colors flex-shrink-0 ${
                  autoTrainingConfig.auto_training_enabled
                    ? 'bg-gradient-accent'
                    : isDark ? 'bg-dark-600' : 'bg-slate-300'
                }`}
              >
                <div className={`absolute top-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-white shadow-md transition-transform ${
                  autoTrainingConfig.auto_training_enabled ? 'translate-x-6 sm:translate-x-8' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {autoTrainingConfig.last_auto_sync && (
              <div className={`flex items-center gap-2 text-xs sm:text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                <Clock className="w-4 h-4" />
                Last synced: {formatDate(autoTrainingConfig.last_auto_sync)}
              </div>
            )}
          </div>

          {/* Platform Insights Panel */}
          {connectedIntegrations.length > 0 && (
            <div className={`p-4 sm:p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${
                    insights?.overallStatus === 'healthy' ? 'bg-accent-green/10' :
                    insights?.overallStatus === 'needs_attention' ? 'bg-accent-orange/10' : 'bg-accent-red/10'
                  }`}>
                    <BarChart3 className={`w-5 h-5 sm:w-6 sm:h-6 ${
                      insights?.overallStatus === 'healthy' ? 'text-accent-green' :
                      insights?.overallStatus === 'needs_attention' ? 'text-accent-orange' : 'text-accent-red'
                    }`} />
                  </div>
                  <div>
                    <h3 className={`text-base sm:text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      Platform Insights
                    </h3>
                    <p className={`text-xs sm:text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                      AI-powered analysis of your connected platforms
                    </p>
                  </div>
                </div>
                <button
                  onClick={fetchInsights}
                  disabled={isLoadingInsights}
                  className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                    isDark ? 'bg-dark-700 hover:bg-dark-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                  }`}
                >
                  <RefreshCw className={`w-4 h-4 ${isLoadingInsights ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Analyze</span>
                </button>
              </div>

              {isLoadingInsights ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-accent-purple" />
                </div>
              ) : insights ? (
                <div className="space-y-4">
                  {/* Overall Score */}
                  <div className={`p-3 sm:p-4 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-slate-50'}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={`text-2xl sm:text-3xl font-bold ${
                          insights.overallScore >= 80 ? 'text-accent-green' :
                          insights.overallScore >= 50 ? 'text-accent-orange' : 'text-accent-red'
                        }`}>
                          {insights.overallScore}
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            Health Score
                          </p>
                          <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                            {insights.overallStatus === 'healthy' ? 'All platforms performing well' :
                             insights.overallStatus === 'needs_attention' ? 'Some platforms need attention' : 'Critical issues detected'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {insights.overallScore >= 80 ? (
                          <TrendingUp className="w-5 h-5 text-accent-green" />
                        ) : (
                          <TrendingDown className="w-5 h-5 text-accent-orange" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Critical Suggestions */}
                  {insights.criticalSuggestions?.length > 0 && (
                    <div className={`p-3 sm:p-4 rounded-xl border-l-4 border-accent-orange ${isDark ? 'bg-accent-orange/10' : 'bg-orange-50'}`}>
                      <div className="flex items-start gap-3">
                        <Lightbulb className="w-5 h-5 text-accent-orange flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            Priority Actions
                          </p>
                          <div className="space-y-2">
                            {insights.criticalSuggestions.map((suggestion, idx) => (
                              <div key={idx} className={`text-xs sm:text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                                <span className="font-medium text-accent-orange">{suggestion.platform}:</span>{' '}
                                <span className="break-words">{suggestion.description}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Platform Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {insights.platforms.map((platform) => {
                      // Map platform display name to provider key
                      const providerMap: Record<string, string> = {
                        'Facebook': 'facebook',
                        'Instagram': 'instagram',
                        'Gmail': 'gmail',
                        'Slack': 'slack',
                        'Notion': 'notion',
                        'Trello': 'trello',
                        'Twitter': 'twitter',
                        'Asana': 'asana',
                        'WhatsApp': 'whatsapp',
                      };
                      const providerKey = providerMap[platform.platform] || platform.platform.toLowerCase();
                      const needsSync = platform.metrics.contentCount === 0 || platform.suggestions.some(s => s.title === 'Start Syncing' || s.title === 'Build Content Library');

                      return (
                        <div
                          key={platform.platform}
                          className={`p-3 sm:p-4 rounded-xl border ${
                            platform.status === 'healthy' ? 'border-accent-green/30' :
                            platform.status === 'needs_attention' ? 'border-accent-orange/30' : 'border-accent-red/30'
                          } ${isDark ? 'bg-dark-700' : 'bg-white'}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className={`font-medium text-sm sm:text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {platform.platform}
                            </span>
                            <span className={`text-xs sm:text-sm font-semibold ${
                              platform.status === 'healthy' ? 'text-accent-green' :
                              platform.status === 'needs_attention' ? 'text-accent-orange' : 'text-accent-red'
                            }`}>
                              {platform.score}%
                            </span>
                          </div>
                          <div className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                            <div className="flex flex-wrap gap-x-3 gap-y-1">
                              <span>{platform.metrics.contentCount} posts</span>
                              <span>{platform.metrics.engagement} engagements</span>
                            </div>
                          </div>
                          {platform.suggestions[0] && (
                            needsSync ? (
                              <button
                                onClick={() => handleManualSync(providerKey)}
                                disabled={isSyncing}
                                className={`text-xs mt-2 text-accent-purple hover:underline cursor-pointer flex items-center gap-1 ${isSyncing ? 'opacity-50' : ''}`}
                              >
                                {isSyncing ? <RefreshCw className="w-3 h-3 animate-spin" /> : null}
                                {platform.suggestions[0].title}
                              </button>
                            ) : (
                              <p className={`text-xs mt-2 ${
                                platform.suggestions[0].type === 'warning' ? 'text-accent-orange' :
                                platform.suggestions[0].type === 'success' ? 'text-accent-green' : isDark ? 'text-text-muted' : 'text-slate-500'
                              }`}>
                                {platform.suggestions[0].title}
                              </p>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {insights.lastAnalyzed && (
                    <p className={`text-xs text-center ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                      Last analyzed: {formatDate(insights.lastAnalyzed)}
                    </p>
                  )}
                </div>
              ) : (
                <div className={`text-center py-6 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Click "Analyze" to get platform insights</p>
                </div>
              )}
            </div>
          )}

          {/* Connected Integrations */}
          <div className={`p-4 sm:p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Data Sources
              </h3>
              <a
                href="/dashboard/integrations"
                className="text-sm text-accent-purple hover:underline flex items-center gap-1"
              >
                <Link2 className="w-4 h-4" />
                Manage Integrations
              </a>
            </div>

            {connectedIntegrations.length === 0 ? (
              <div className={`text-center py-8 rounded-xl border border-dashed ${isDark ? 'border-border bg-dark-700/50' : 'border-slate-200 bg-slate-50'}`}>
                <Link2 className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                <p className={`font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>No integrations connected</p>
                <p className={`text-sm mb-4 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  Connect integrations to enable auto-training
                </p>
                <a
                  href="/dashboard/integrations"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Connect Integration
                </a>
              </div>
            ) : (
              <div className="space-y-3">
                {connectedIntegrations.map((integration) => {
                  const isSelected = autoTrainingConfig.auto_training_sources?.includes(integration.provider);
                  const iconUrl = getProviderIcon(integration.provider);

                  return (
                    <div
                      key={integration.id}
                      onClick={() => toggleSource(integration.provider)}
                      className={`p-4 rounded-xl border cursor-pointer transition-all ${
                        isSelected
                          ? 'border-accent-purple bg-accent-purple/5'
                          : isDark ? 'border-border hover:border-border-light bg-dark-700' : 'border-slate-200 hover:border-slate-300 bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-dark-600' : 'bg-white'}`}>
                            {iconUrl ? (
                              <img src={iconUrl} alt={integration.provider} className="w-5 h-5" />
                            ) : (
                              <Globe className="w-5 h-5 text-accent-purple" />
                            )}
                          </div>
                          <div>
                            <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {getProviderLabel(integration.provider)}
                            </p>
                            <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                              Connected
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleManualSync(integration.provider);
                            }}
                            disabled={isSyncing}
                            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-600' : 'hover:bg-slate-200'}`}
                            title="Sync now"
                          >
                            <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''} ${isDark ? 'text-text-muted' : 'text-slate-500'}`} />
                          </button>
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                            isSelected ? 'bg-accent-purple' : isDark ? 'bg-dark-600 border border-border' : 'bg-white border border-slate-300'
                          }`}>
                            {isSelected && <Check className="w-4 h-4 text-white" />}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sync Interval */}
          <div className={`p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Sync Frequency
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { value: 'hourly', label: 'Hourly' },
                { value: 'every_6_hours', label: '6 Hours' },
                { value: 'every_12_hours', label: '12 Hours' },
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
              ].map((interval) => (
                <button
                  key={interval.value}
                  onClick={() => setAutoTrainingConfig(prev => ({ ...prev, auto_training_interval: interval.value }))}
                  className={`p-3 rounded-xl text-sm font-medium transition-colors ${
                    autoTrainingConfig.auto_training_interval === interval.value
                      ? 'bg-gradient-accent text-white'
                      : isDark ? 'bg-dark-700 text-text-secondary hover:bg-dark-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {interval.label}
                </button>
              ))}
            </div>
          </div>

          {/* Sync History */}
          <div className={`p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Sync History
              </h3>
              <History className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
            </div>

            {syncHistory.length === 0 ? (
              <div className={`text-center py-6 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No sync history yet</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {syncHistory.map((sync) => (
                  <div
                    key={sync.id}
                    className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-slate-50'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-dark-600' : 'bg-white'}`}>
                        {getProviderIcon(sync.provider) ? (
                          <img src={getProviderIcon(sync.provider)!} alt={sync.provider} className="w-4 h-4" />
                        ) : (
                          <Globe className="w-4 h-4" />
                        )}
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {getProviderLabel(sync.provider)}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                          {formatDate(sync.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {sync.items_synced !== null && (
                        <span className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                          {sync.items_synced} items
                        </span>
                      )}
                      <span className={`badge ${
                        sync.status === 'completed'
                          ? 'bg-accent-green/20 text-accent-green'
                          : sync.status === 'in_progress'
                          ? 'bg-accent-orange/20 text-accent-orange'
                          : 'bg-accent-red/20 text-accent-red'
                      }`}>
                        {sync.status === 'completed' && <Check className="w-3 h-3 mr-1" />}
                        {sync.status === 'in_progress' && <RefreshCw className="w-3 h-3 mr-1 animate-spin" />}
                        {sync.status === 'failed' && <AlertCircle className="w-3 h-3 mr-1" />}
                        {sync.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleSaveAutoTraining}
              disabled={isSavingAutoTraining}
              className="btn-primary flex-1"
            >
              {isSavingAutoTraining ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Auto-Training Settings'
              )}
            </button>
            <button
              onClick={() => handleManualSync()}
              disabled={isSyncing || autoTrainingConfig.auto_training_sources?.length === 0}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 disabled:opacity-50 ${
                isDark
                  ? 'bg-dark-700 border border-border-light hover:bg-dark-600 text-white'
                  : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm'
              }`}
            >
              {isSyncing ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Play className="w-5 h-5" />
              )}
              Sync Now
            </button>
          </div>
        </motion.div>
      )}

      {/* Configuration Tab */}
      {activeTab === 'config' && (
        <motion.div variants={fadeInUp} className="space-y-6">
          {/* Custom Instructions */}
          <div className={`p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Custom Instructions
            </h3>
            <textarea
              value={config.custom_instructions || ''}
              onChange={(e) =>
                setConfig({ ...config, custom_instructions: e.target.value })
              }
              placeholder="Add custom instructions for your AI employees...

Example:
- Always mention our company name 'Acme Corp' when introducing yourself
- Respond in a friendly but professional manner
- Include relevant product links when discussing features
- Follow up questions with offers to help further"
              rows={6}
              className={`w-full px-4 py-3 rounded-xl border resize-none focus:outline-none focus:border-accent-purple focus:ring-2 focus:ring-accent-purple/20 ${isDark ? 'bg-dark-800 border-border text-white placeholder-text-muted' : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'}`}
            />
            <p className={`text-xs mt-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
              These instructions will be applied to all AI employees
            </p>
          </div>

          {/* Tone */}
          <div className={`p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Response Tone
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {['professional', 'casual', 'friendly', 'formal'].map((tone) => (
                <button
                  key={tone}
                  onClick={() => setConfig({ ...config, tone })}
                  className={`p-3 rounded-xl text-sm font-medium transition-colors capitalize ${
                    config.tone === tone
                      ? 'bg-gradient-accent text-white'
                      : isDark ? 'bg-dark-700 text-text-secondary hover:bg-dark-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {tone}
                </button>
              ))}
            </div>
          </div>

          {/* Response Length */}
          <div className={`p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Response Length
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {['concise', 'balanced', 'detailed'].map((length) => (
                <button
                  key={length}
                  onClick={() => setConfig({ ...config, response_length: length })}
                  className={`p-3 rounded-xl text-sm font-medium transition-colors capitalize ${
                    config.response_length === length
                      ? 'bg-gradient-accent text-white'
                      : isDark ? 'bg-dark-700 text-text-secondary hover:bg-dark-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {length}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleSaveConfig}
            disabled={isSavingConfig}
            className="btn-primary disabled:opacity-50"
          >
            {isSavingConfig ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Configuration'
            )}
          </button>
        </motion.div>
      )}
    </motion.div>
  );
}
